import { AloftDate, CommentaryV2, isDefined } from '@aloft/toolkit-shared';
import { Flex, Icon, ProductColor, SmallLoader, Tabs, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';

import { ValuationTablePropertyHeaderAddress } from '../../../components/CraftsmanPropertyHeader/internal/ValuationTablePropertyHeaderAddress.tsx';
import { ValuationTablePropertyHeaderTitle } from '../../../components/CraftsmanPropertyHeader/internal/ValuationTablePropertyHeaderTitle.tsx';
import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import {
  selectCompSequence,
  selectPropertyTitleById,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectAddress } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectTransactionById } from '../../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { selectSelectedPriorSalesPropertyId } from '../../../redux/features/selectors/uiSelectors.ts';
import { setSelectedPriorSalesPropertyId } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { getColorForStatusOrSubject } from '../../../utils/getColorForStatusOrSubject.ts';
import { getPropertyPinStatus } from '../../../utils/getPropertyPinStatus.ts';
import { Tkv2MlsDataSheet } from '../../referenceView/MLSDataSheet/Tkv2MlsDataSheet.tsx';
import { ReferenceViewID, formatReferenceViewID } from '../../referenceView/index.ts';

export const PriorSalesTabPanels = () => null;

type LeftPanelPropertyBoxProps = {
  propertyId: string;
};

const LeftPanelPropertyBox = ({ propertyId }: LeftPanelPropertyBoxProps) => {
  const dispatch = useAppDispatch();

  const selectedPriorSalesPropertyId = useAppSelector(selectSelectedPriorSalesPropertyId);
  const address = useAppSelector((state) => selectAddress(state, propertyId));
  const title = useAppSelector((state) => selectPropertyTitleById(state, propertyId));
  const transaction = useAppSelector((state) => selectTransactionById(state, propertyId));

  const propertyPriorSales = useAppSelector(selectPriorSales)[propertyId];

  const nbOfSales = propertyPriorSales?.length ?? 0;
  const nbOfSalesText = nbOfSales === 1 ? '1 Transaction' : `${nbOfSales} Transactions`;

  if (!propertyPriorSales) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100px"
        height="100px"
        backgroundColor="surface1"
        border="1px solid strokeDefault"
        borderRadius="8px"
      >
        <SmallLoader />
      </Flex>
    );
  }

  const isSubject = propertyId === 'subject';
  const propertyStatus = isSubject ? 'Subject' : (transaction?.status ?? 'Off-Market');

  const pinStatus = getPropertyPinStatus(propertyStatus);
  const pinColor = getColorForStatusOrSubject(propertyStatus);

  const isDisabled = nbOfSales === 0;
  const isSelected = selectedPriorSalesPropertyId === propertyId;
  const nbOfSalesTextColor = isDisabled ? 'textTertiary' : 'buttonDefault';

  let backgroundColor: ProductColor = 'surface1';
  if (isDisabled) {
    backgroundColor = 'surface2';
  } else if (isSelected) {
    backgroundColor = 'selected4';
  }
  const borderColor: ProductColor = isSelected ? 'surfaceAccent4' : 'strokeDefault';

  return (
    <Flex
      flexDirection="column"
      backgroundColor={backgroundColor}
      border={`1px solid ${borderColor}`}
      borderRadius="8px"
      width="fit-content"
      padding="0.5rem"
      gap="0.25rem"
      cursor={isDisabled ? 'default' : 'pointer'}
      onClick={() => {
        if (isDisabled) return;

        dispatch(setSelectedPriorSalesPropertyId(propertyId));
      }}
    >
      <ValuationTablePropertyHeaderTitle
        hideActions
        hideDistanceToSubject
        title={title}
        propertyId={propertyId}
        pinColor={pinColor}
        pinStatus={pinStatus}
      />
      <ValuationTablePropertyHeaderAddress address={address} />
      <Flex alignItems="center" gap="0.5rem">
        <Typography color={nbOfSalesTextColor}>{nbOfSalesText}</Typography>
        <Icon icon="arrow-right" color={nbOfSalesTextColor} />
      </Flex>
    </Flex>
  );
};

PriorSalesTabPanels.LeftPanel = () => {
  const compSequence = useAppSelector(selectCompSequence);

  const { control, setValue } = useFormContext<CommentaryV2>();

  const commentaryFieldName: FieldPath<CommentaryV2> = 'priorSales.commentary';

  const { errors } = useAppraisifyValidation();
  const { priorSalesCommentary } = errors?.Commentary ?? undefined;

  return (
    <Flex flexDirection="column" padding="1rem" gap="1.5rem" width="100%">
      <Typography variant="h2">Prior Sales Analysis</Typography>
      <LeftPanelPropertyBox propertyId="subject" />
      <Flex flexWrap="wrap" gap="1rem">
        {compSequence.map((propertyId) => (
          <LeftPanelPropertyBox key={propertyId} propertyId={propertyId} />
        ))}
      </Flex>

      <FormTextarea
        name={commentaryFieldName}
        control={control}
        label="Prior Sales Commentary"
        placeholder=""
        isRequired
        column
        resize="vertical"
        customErrorMessage={priorSalesCommentary?.message}
        onClear={() => setValue(commentaryFieldName, undefined)}
        labelVariant="label"
        labelColor="textPrimary"
      />
    </Flex>
  );
};

PriorSalesTabPanels.RightPanel = () => {
  const [activeTab, setActiveTab] = useState<string>(String(0));

  const selectedPriorSalesPropertyId = useAppSelector(selectSelectedPriorSalesPropertyId);
  const priorSales = useAppSelector(selectPriorSales);

  if (Object.entries(priorSales).length === 0) return <LoadingScreen />;

  const selectedPriorSalesData = priorSales[selectedPriorSalesPropertyId] || [];
  const tabs = selectedPriorSalesData.map((listingData, i) => {
    const { ListingContractDate, MLSName, ListingId } = listingData;
    const mlsName = MLSName?.toLocaleUpperCase();
    const listingId = ListingId && `#${ListingId.toString()}`;
    const listingDate =
      ListingContractDate &&
      `(${AloftDate.fromIsoString(ListingContractDate).format('MM/dd/yyyy')})`;

    return {
      value: String(i),
      label: [mlsName, listingId, listingDate].filter(isDefined).join(' '),
    };
  });

  let referenceViewID: ReferenceViewID;
  if (selectedPriorSalesPropertyId === 'subject') {
    referenceViewID = formatReferenceViewID({ type: 'subject' });
  } else {
    referenceViewID = formatReferenceViewID({
      type: 'comparable',
      compID: selectedPriorSalesPropertyId,
    });
  }

  const hasNoPriorSales = selectedPriorSalesData.length === 0;
  const listingData = selectedPriorSalesData[Number(activeTab)];

  if (hasNoPriorSales) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
        padding="1rem"
        gap="1rem"
      >
        <Icon icon="error-warning" color="alertWarning" size="xl" />
        <Typography variant="h3">
          This property has no Prior Sales Data, please select another property
        </Typography>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" padding="1rem" gap="1.5rem" width="100%">
      <Flex minHeight="44px" overflowX="auto" __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}>
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
      </Flex>
      {!listingData && <SmallLoader />}
      {listingData && (
        <Tkv2MlsDataSheet
          isPopout={false}
          listingData={listingData}
          referenceViewID={referenceViewID}
        />
      )}
    </Flex>
  );
};
